import type { ProductListItem, SeoData } from "@/lib/storefront-types";

// Kept dependency-free (type-only imports) so the module can be unit-tested
// under `node --experimental-strip-types --test`, like seo-schema.ts.

export function formatPrice(price: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(price);
}

/** A valid offer is an integer percentage between 1 and 80. */
export function resolveOfferRate(product: ProductListItem) {
  return typeof product.offerPercentageRate === "number" &&
    Number.isInteger(product.offerPercentageRate) &&
    product.offerPercentageRate >= 1 &&
    product.offerPercentageRate <= 80
    ? product.offerPercentageRate
    : null;
}

/**
 * Root-relative path of a product's detail page. Returns null when either
 * slug is missing so callers can skip linking rather than emit a broken URL.
 */
export function buildProductPath(
  product: Pick<ProductListItem, "slug"> & {
    category: Pick<ProductListItem["category"], "slug">;
  },
): string | null {
  const categorySlug = product.category.slug.trim();
  const productSlug = product.slug.trim();

  if (!categorySlug || !productSlug) {
    return null;
  }

  return `/menu/${categorySlug}/${productSlug}`;
}

/**
 * Splits a trailing "(variant)" off a product name so the detail-page hero can
 * render it as a pill beside the title — e.g.
 * "Ayamase Original © (Large Pot)" → { title: "Ayamase Original ©", variant: "Large Pot" }.
 * Names with no trailing parenthetical return the whole name and a null variant
 * ("Coke" → { title: "Coke", variant: null }).
 */
export function splitProductTitle(name: string): {
  title: string;
  variant: string | null;
} {
  const match = name.match(/^(.+?)\s*\(([^()]+)\)\s*$/);
  if (match && match[1].trim()) {
    return { title: match[1].trim(), variant: match[2].trim() };
  }
  return { title: name.trim(), variant: null };
}

/**
 * Alt text for a product/category image. Editors sometimes leave alt blank or
 * the CMS falls back to the upload's filename ("jollof-rice.webp"), neither of
 * which helps screen readers or image SEO — both fall back to the entity name.
 * A real alt is kept (trimmed, matching the previous `alt?.trim() ||` callers).
 */
export function resolveMediaAlt(
  alt: string | null | undefined,
  fallbackName: string,
): string {
  const trimmed = alt?.trim() ?? "";
  if (!trimmed || /\.(svg|png|jpe?g|webp|gif|avif|jfif)$/i.test(trimmed)) {
    return fallbackName;
  }
  return trimmed;
}

/**
 * Clips plain text to a maximum length on a word boundary — used for meta
 * descriptions synthesized from product copy.
 */
export function clipText(text: string, maxLength = 160) {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length <= maxLength) {
    return collapsed;
  }

  const clipped = collapsed.slice(0, maxLength + 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, lastSpace > 0 ? lastSpace : maxLength).trimEnd()}…`;
}

/**
 * SEO for a product detail page. Editor values from the product's SEO tab win;
 * blank ones fall back to the synthesized title/description/image, so an older
 * CMS that sends no `seo` renders exactly as before. Editor-supplied
 * descriptions are used as written (not clipped). Indexability is the
 * category's flags OR'd with the product's own. `pageTitle` and `canonicalUrl`
 * are deliberately not editor-driven — `canonicalPath` stays the single source
 * of canonical truth.
 */
export function resolveProductSeo(
  product: Pick<
    ProductListItem,
    "name" | "summary" | "description" | "image" | "seo"
  >,
  categorySeo: Pick<SeoData, "noIndex" | "noFollow">,
  brandName: string,
  canonicalPath: string,
): SeoData {
  return {
    pageTitle: product.name,
    seoTitle: product.seo?.seoTitle?.trim() || `${product.name} | ${brandName}`,
    seoDescription:
      product.seo?.seoDescription?.trim() ||
      clipText(product.summary || product.description),
    seoImage: product.seo?.seoImage ?? product.image ?? null,
    canonicalUrl: canonicalPath,
    noIndex: categorySeo.noIndex || product.seo?.noIndex === true,
    noFollow: categorySeo.noFollow || product.seo?.noFollow === true,
  };
}
